import { BrokeragePricing } from '../../../slices/marketplace/pricing/models/brokerage-pricing.model';
import { BrokerageType } from '../../../slices/marketplace/pricing/models/brokerage-type.enum';
import { MarketplacePricing } from '../../../slices/marketplace/pricing/models/marketplace-pricing.model';
import { TransactionFeeScheduleItem } from '../../../slices/marketplace/pricing/models/transaction-fee-schedule-item.model';
import { TransactionFees } from '../../../slices/marketplace/pricing/models/transaction-fees.model';
import { CurrencyFormatter } from '../../../utils/currencyFormatter';
import {
    ComingSoonCommercialTermsTableData,
    CommingSoonCommercialTermsRow,
} from '../RequestPreOrderEngagementAgreementModal/Models/Quote';

export class ComingSoonCommercialTermsCalculator {
    static calculateCommercialTerms(
        fteW2Dvms: number,
        ltmRevenue: number,
        type: BrokerageType,
        marketplacePricing: MarketplacePricing
    ): ComingSoonCommercialTermsTableData {
        ltmRevenue = Number(ltmRevenue.toString().replace(/,/g, ''));
        fteW2Dvms = Number(fteW2Dvms);
        const pricing =
            type === BrokerageType.Discount
                ? marketplacePricing.discountBrokeragePricing
                : marketplacePricing.fullServiceBrokeragePricing;
        const transactionFees = pricing.transactionFees;
        const scheduleRow = this.getScheduleRow(ltmRevenue, pricing);
        return {
            transactionFee: this.calculateTxFee(ltmRevenue, pricing),
            processFees: [
                this.calculateIdNdaFee(scheduleRow, transactionFees),
                this.calculateDataNdaFee(scheduleRow, transactionFees),
                this.calculateFinancialModelFee(scheduleRow, transactionFees),
                this.calculateBuyerMeetingCredit(transactionFees, type),
            ],
            otherFees: [
                this.calculateApplicationFee(fteW2Dvms, transactionFees),
                ...(type === BrokerageType.FullService
                    ? [
                          this.calculateMonthlyRetainerFee(
                              scheduleRow,
                              transactionFees
                          ),
                      ]
                    : []),
                this.calculateLateFee(scheduleRow, transactionFees),
                this.calculateSuspensionFee(scheduleRow, transactionFees, type),
                this.calculateTempoChangeFee(scheduleRow, transactionFees),
            ],
            tailPeriod: `${pricing.transactionFees.tailPeriodMonths} Months`,
        };
    }

    private static getScheduleRow(
        ltmRevenue: number,
        pricing: BrokeragePricing
    ): TransactionFeeScheduleItem | undefined {
        const sorted = [...pricing.transactionFeeSchedule].sort((a, b) => a.revenueMax - b.revenueMax);
        return sorted.find(s => ltmRevenue < s.revenueMax) ?? sorted[sorted.length - 1];
    }

    private static calculateTxFee(
        ltmRevenue: number,
        pricing: BrokeragePricing
    ): number {
        const row = this.getScheduleRow(ltmRevenue, pricing);
        return row?.feePercent ?? 0;
    }

    private static calculateIdNdaFee(
        scheduleRow: TransactionFeeScheduleItem | undefined,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const price = CurrencyFormatter.formatToDollarValue(
            scheduleRow?.idNdaFeePerBuyer ?? 0
        );
        return {
            id: 1,
            label: 'Buyer ID NDA Fee',
            toolTip:
                "Once a buyer signs an ID NDA with the seller, the seller's indentity is disclosed to that buyer. The seller selects which of the interested buyers it wishes to send an ID NDA. The seller is then charged ONLY for the number of buyers that actually elect to sign the ID NDA.",
            feeAmountValue: `${price} per Buyer (first ${fees.idNdaFirstXCount} count as 1)`,
            refundable: fees.idNdaFeeRefundable,
            creditable: fees.idNdaFeeCreditable,
        };
    }

    private static calculateDataNdaFee(
        scheduleRow: TransactionFeeScheduleItem | undefined,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const price = CurrencyFormatter.formatToDollarValue(
            scheduleRow?.dataNdaFeePerBuyer ?? 0
        );
        return {
            id: 2,
            label: 'PIP/PIQ Provision Fee',
            toolTip:
                'Buyers must sign a Data NDA before they are eligible to receive either an practice investment presentation ("PIP") or the detailed financial model. The seller selects which of the interested buyers it wishes to send a Data NDA. The seller is then charged ONLY for the number of buyers that actually elect to sign the Data NDA.',
            feeAmountValue: `${price} per Buyer (first ${fees.dataNdaFirstXCount} count as 1)`,
            refundable: fees.dataNdaFeeRefundable,
            creditable: fees.dataNdaFeeCreditable,
        };
    }

    private static calculateFinancialModelFee(
        scheduleRow: TransactionFeeScheduleItem | undefined,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const price = CurrencyFormatter.formatToDollarValue(
            scheduleRow?.financialModelFeePerBuyer ?? 0
        );
        return {
            id: 3,
            label: 'DFM Provision Fee',
            toolTip:
                'The seller selects which of the interested buyers it wishes to allow to see the detailed financial model. The seller is then charged for each of these buyers which view the detailed financial model.',
            feeAmountValue: `${price} per Buyer (first ${fees.financialModelFirstXCount} count as 1)`,
            refundable: fees.financialModelFeeRefundable,
            creditable: fees.financialModelFeeCreditable,
        };
    }

    private static calculateBuyerMeetingCredit(
        fees: TransactionFees,
        brokerageType: BrokerageType
    ): CommingSoonCommercialTermsRow {
        const virtualMeetingCredit = CurrencyFormatter.formatToDollarValue(
            fees.buyerMeetingVirtualCredit
        );
        const inPersonMeetingCredit = CurrencyFormatter.formatToDollarValue(
            fees.buyerMeetingInPersonCredit
        );
        return {
            id: 4,
            label: 'Buyer Meeting Credits',
            toolTip:
                "Buyers are charged a refundable meeting fee for either an in-person or virtual meeting with the seller. This fee is refunded to the buyer if the seller is unable or unwilling to attend the meeting. Meetings are typically 3-5 hours in length and may include discussion over a meal. There is no meeting fee for the seller. VetValue credits 50% of the buyer's meeting fee towards the seller's transaction fee. ",
            feeAmountValue: `${virtualMeetingCredit} credit per virtual meeting \n ${inPersonMeetingCredit} credit per in-person meeting`,
            feeAmountValueTooltip:
                brokerageType === BrokerageType.FullService
                    ? 'Net of advisor travel expenses'
                    : undefined,
            refundable: fees.buyerMeetingRefundable,
            creditable: fees.buyerMeetingCreditable,
        };
    }

    private static calculateApplicationFee(
        fteW2Dvms: number,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const totalPrice =
            fees.applicationFee + fees.applicationFeePerDvm * fteW2Dvms;
        const price = CurrencyFormatter.formatToDollarValue(totalPrice);
        return {
            id: 5,
            label: 'Application Fee',
            toolTip:
                'Charged at the time of application to engage this platform',
            feeAmountValue: `${price}`,
            refundable: fees.applicationFeeRefundable,
            creditable: fees.applicationFeeCreditable,
        };
    }

    private static calculateMonthlyRetainerFee(
        scheduleRow: TransactionFeeScheduleItem | undefined,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const totalPrice = scheduleRow?.monthlyRetainerFee ?? 0;
        const price = CurrencyFormatter.formatToDollarValue(totalPrice);
        return {
            id: 6,
            label: 'Monthly Engagement Fee',
            toolTip:
                'An engagement fee is charged at the beginning of each month, starting at the effective date of the engagement. During any period for which the seller elects to suspend the engagement, this fee is reduce by 50% for the duration of that suspendion.',
            feeAmountValue: `${price}  per month`,
            refundable: fees.monthlyRetainerFeeRefundable,
            creditable: fees.monthlyRetainerFeeCreditable,
        };
    }

    private static calculateLateFee(
        scheduleRow: TransactionFeeScheduleItem | undefined,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const firstOccurrence = CurrencyFormatter.formatToDollarValue(
            scheduleRow?.lateFeeFirstOccurrence ?? 0
        );
        const additionalOccurrence = CurrencyFormatter.formatToDollarValue(
            scheduleRow?.lateFeeAdditionalOccurrence ?? 0
        );
        return {
            id: 7,
            label: 'Late Fee',
            toolTip:
                "Late fees are charged each time the seller does not complete a step by that step's deadline.",
            feeAmountValue: `${firstOccurrence} per 1st occurrence, \n ${additionalOccurrence} for each additional occurrence (days late)`,
            refundable: fees.lateFeeRefundable,
            creditable: fees.lateFeeCreditable,
        };
    }

    private static calculateSuspensionFee(
        scheduleRow: TransactionFeeScheduleItem | undefined,
        fees: TransactionFees,
        brokerageType: BrokerageType
    ): CommingSoonCommercialTermsRow {
        const totalPrice = scheduleRow?.suspensionFee ?? 0;
        const price = CurrencyFormatter.formatToDollarValue(totalPrice);

        const suspensionFeeTooltip =
            brokerageType === BrokerageType.FullService
                ? 'Suspension fees begin on the retainer renewal date. During suspension, the suspension fee replaces the monthly retainer. When the engagement resumes, regular retainer fees restart immediately, and the renewal date is reset to the resumption date.'
                : 'A suspension fee is charged for each month the engagement has been suspended by the seller.';

        return {
            id: 8,
            label: 'Monthly Suspension Fee',
            toolTip: suspensionFeeTooltip,
            feeAmountValue: `${price} per month`,
            refundable: fees.suspensionFeeRefundable,
            creditable: fees.suspensionFeeCreditable,
        };
    }

    private static calculateTempoChangeFee(
        scheduleRow: TransactionFeeScheduleItem | undefined,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const price = CurrencyFormatter.formatToDollarValue(
            scheduleRow?.tempoChangeFee ?? 0
        );
        return {
            id: 9,
            label: 'Tempo Change Fee',
            toolTip: undefined,
            feeAmountValue: `${price}`,
            refundable: fees.tempoChangeFeeRefundable,
            creditable: fees.tempoChangeFeeCreditable,
        };
    }
}
